import type { FirestoreTimestamp } from './timestamp'
import type { AppPlatform, SetTimeSignal } from './watchlist'
import type { SourceRef } from '../sourceIdentity'

// 'multi' means the URL resolved to several events, so we seeded them all but
// left the suggester to pick which one they meant.
export type EventSuggestionStatus = 'pending' | 'seeded' | 'multi' | 'failed' | 'noise'

export interface EventSuggestion {
  id: string
  url: string
  groupIds: string[]
  submittedBy: string
  createdAt: FirestoreTimestamp
  status: EventSuggestionStatus
  eventIds?: string[]
  // Recorded by the seeding routine, which is the only thing that knows one URL
  // resolved to several events. Fulfillment reads it rather than re-deriving it.
  outcome?: 'single' | 'multi'
  fulfilledAt?: FirestoreTimestamp
  processedAt?: FirestoreTimestamp
  failureNote?: string
  // Failed seed attempts before status flips permanently to 'failed' — see
  // MAX_SEED_RETRIES in scripts/mark-suggestion-processed.ts. A transient failure
  // (bot-block, rate limit) gets a few scheduled runs to succeed before giving up.
  retryCount?: number
  // Last failed attempt; list-event-suggestions.ts backs off from it so an
  // on-demand run minutes later doesn't burn the remaining retries.
  lastAttemptAt?: FirestoreTimestamp
  // Set by the run that listed it for seeding. Routine runs can overlap now
  // that suggestions fire them on demand; a live claim keeps a second run off it.
  claimedBy?: string
  claimedAt?: FirestoreTimestamp
}

// scheduleLeads/{leadId} — a user telling us an event's set times have been
// published. The Schedule tab's empty state is the highest-intent moment we
// have (they came looking for the timetable), and it is otherwise a dead end:
// nothing in the pipeline looks back at an event whose schedule wasn't out when
// we seeded it. Drained by the schedule-leads routine (fired as each lead lands),
// with the admin queue as a manual override.
// 'superseded' — a system lead filed for an association that has since been
// replaced; it no longer blocks the new source's leads. Users' leads never get it.
export type ScheduleLeadStatus = 'pending' | 'seeded' | 'failed' | 'noise' | 'superseded'

export interface ScheduleLead {
  id: string
  eventId: string
  /** Where they saw them. Optional — an app-only schedule has no link to give. */
  url?: string
  /** A user's uid, or SYSTEM_LEAD_SUBMITTER when watchSetTimes filed it. */
  submittedBy: string
  createdAt: FirestoreTimestamp
  status: ScheduleLeadStatus
  processedAt?: FirestoreTimestamp
  /** An admin's uid, or `routine:schedule-leads` when the routine resolved it. */
  processedBy?: string
  /** System leads only: what watchSetTimes saw — `search` means it saw nothing and wants a look. */
  signal?: SetTimeSignal
  /** System leads only: the evidence, e.g. "Festival Dust: setTimesEnabled=true". */
  signalNote?: string
  /** System leads only: the source fingerprint it was filed for — copied to setTimeWatch.applied once seeded. */
  fingerprint?: string
  /** System leads only: the source that produced `fingerprint`. Absent on leads filed before sourceRefs. */
  sourceRef?: SourceRef
  /** System leads only: setTimeWatch.associationGeneration it was filed under. Absent reads as 0. */
  associationGeneration?: number
  /** superseded only: the generation that replaced it. */
  supersededByGeneration?: number
}

/** submittedBy of a lead watchSetTimes filed. Nobody to notify when it's seeded. */
export const SYSTEM_LEAD_SUBMITTER = 'system:set-time-watch'

export function isSystemLead(lead: Pick<ScheduleLead, 'submittedBy'>): boolean {
  return lead.submittedBy.startsWith('system:')
}

// scheduleLeadAttempts/{eventId} — the schedule-leads routine's working state
// for one event's leads (Admin SDK only; holds no user data). Kept off the lead
// docs so the admin triage rule, which allows only the status fields, still
// accepts updates to leads the routine has touched.
export interface ScheduleLeadAttempts {
  /** The run working on this event. Runs can overlap now that leads fire them. */
  claimedBy?: string
  claimedAt?: FirestoreTimestamp
  /**
   * setTimeWatch.associationGeneration when the run claimed the event. The seed
   * (seed-drafts) and the marker refuse to attribute a result once it moved.
   */
  claimGeneration?: number
  /**
   * The sources the run was claimed against. seed-drafts.ts refuses a draft
   * from the app when it was (or since became) quarantined; Festival Dust and
   * other sources still seed.
   */
  claimSources?: {
    app?: { platform: AppPlatform; key: string; edition?: string; revision?: string }
    appQuarantined?: boolean
    festivalDust?: string
  }
  /** Failed attempts since the last report; reset when a new lead arrives. */
  retryCount: number
  lastAttemptAt?: FirestoreTimestamp
  failureNote?: string
}

// regionRequests/{requestId} — a user telling us their metro isn't covered.
// Regions are hardcoded in shared/regions.ts, so without this the only demand
// signal is admin-side (unmapped cities reported while seeding), which only
// fires where we already happen to seed. Triaged from the admin queue, which
// aggregates by normalized place name so one metro reads as one row.
export type RegionRequestStatus = 'pending' | 'planned' | 'dismissed'

export interface RegionRequest {
  id: string
  /** The city or metro as typed. Aggregation normalizes it, storage doesn't. */
  text: string
  submittedBy: string
  createdAt: FirestoreTimestamp
  status: RegionRequestStatus
  resolvedAt?: FirestoreTimestamp
  resolvedBy?: string
}

// setReports/{reportId} — a user flagging something wrong on one set: its
// times, artist, stage, or the artist's photo or bio. Structured (category +
// set) rather than free-text feedback, so the schedule-leads routine can check
// it against the event's source on its own and an admin only sees what it
// couldn't settle.
export type SetReportCategory = 'times' | 'artist' | 'stage' | 'image' | 'description' | 'other'

export const SET_REPORT_CATEGORIES: readonly SetReportCategory[] = [
  'times', 'artist', 'stage', 'image', 'description', 'other',
]

export type SetReportStatus = 'pending' | 'fixed' | 'dismissed'

/** The routine's read: `not-reproduced` closes the report, the others leave it for an admin. */
export type SetReportVerdict = 'confirmed' | 'not-reproduced' | 'unclear'

export interface SetReport {
  id: string
  eventId: string
  setId: string
  /** The set's artists when it was reported — the set may change or be replaced since. */
  artistIds: string[]
  category: SetReportCategory
  message?: string
  submittedBy: string
  createdAt: FirestoreTimestamp
  status: SetReportStatus
  verdict?: SetReportVerdict
  verdictNote?: string
  /** The page the verdict rests on, or for an image/bio report the suggested replacement. */
  evidenceUrl?: string
  verifiedAt?: FirestoreTimestamp
  resolvedAt?: FirestoreTimestamp
  /** An admin's uid, or `routine:schedule-leads`. */
  resolvedBy?: string
}

// eventReports/{reportId} — a user flagging something wrong on an event's
// listing: it moved venue, changed date, was cancelled, has a dead ticket link,
// is missing artists, has the wrong image, duplicates another event, or isn't a
// real event at all. The event-reports routine re-fetches the event from its
// source, lets the seeding pipeline fix what the source confirms, and records a
// verdict; an admin only sees what it couldn't settle.
export type EventReportCategory =
  | 'venue'
  | 'datetime'
  | 'cancelled'
  | 'tickets'
  | 'lineup'
  | 'image'
  | 'duplicate'
  | 'spam'
  | 'other'

export const EVENT_REPORT_CATEGORIES: readonly EventReportCategory[] = [
  'venue', 'datetime', 'cancelled', 'tickets', 'lineup', 'image', 'duplicate', 'spam', 'other',
]

/** The categories whose report says nothing without a note. */
export const EVENT_REPORT_NOTE_REQUIRED: readonly EventReportCategory[] = ['other']

export interface EventReport {
  id: string
  eventId: string
  category: EventReportCategory
  message?: string
  /** Where the reporter saw the right information — untrusted, the routine checks it. */
  url?: string
  submittedBy: string
  createdAt: FirestoreTimestamp
  /** Same lifecycle and routine verdicts as a set report. */
  status: SetReportStatus
  verdict?: SetReportVerdict
  verdictNote?: string
  evidenceUrl?: string
  verifiedAt?: FirestoreTimestamp
  resolvedAt?: FirestoreTimestamp
  /** An admin's uid, or `routine:event-reports`. */
  resolvedBy?: string
}
